import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isAndroidApp } from "utils/isAndroidApp";
import { registerFirebasePushToken } from "../services/firebase-push";
import { syncFcmTokenToBackend } from "../services/fcm-token";

const PWA_BANNER_DISMISSED_KEY = "pwaInstallBannerDismissed";
const PWA_INSTALLED_KEY = "pwaInstalled";
const FCM_TOKEN_STORAGE_KEY = "fcmPushToken";
const FCM_TOKEN_SYNCED_KEY = "fcmPushTokenSynced";

type PwaPlatform = "android" | "ios" | "other";

interface RelatedApplication {
  id?: string;
  platform?: string;
  url?: string;
}

interface NavigatorWithPwa extends Navigator {
  standalone?: boolean;
  getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
}

interface BeforeInstallPromptChoice {
  outcome: "accepted" | "dismissed";
  platform: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
}

interface PwaInstallContextValue {
  canPromptInstall: boolean;
  dismissInstallBanner: () => void;
  fcmToken: string | null;
  handleInstall: () => Promise<void>;
  isHandleInstallLoading: boolean;
  installPwa: () => Promise<void>;
  isNotificationPermissionRequesting: boolean;
  isNotificationSupported: boolean;
  isInstallBannerVisible: boolean;
  isInstalling: boolean;
  notificationPermission: NotificationPermission | "unsupported";
  platform: PwaPlatform;
  requestNotificationPermission: () => Promise<NotificationPermission | "unsupported">;
}

const defaultContextValue: PwaInstallContextValue = {
  canPromptInstall: false,
  dismissInstallBanner: () => undefined,
  fcmToken: null,
  handleInstall: async () => undefined,
  isHandleInstallLoading: false,
  installPwa: async () => undefined,
  isNotificationPermissionRequesting: false,
  isNotificationSupported: false,
  isInstallBannerVisible: false,
  isInstalling: false,
  notificationPermission: "unsupported",
  platform: "other",
  requestNotificationPermission: async () => "unsupported",
};

export const PwaInstallContext = createContext<PwaInstallContextValue>(defaultContextValue);

/**
 * Reports non-fatal PWA errors to the shared browser error reporter when available.
 */
const reportPwaError = (error: unknown, context: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  const errorReporting = (
    window as Window & {
      errorReporting?: { captureException: (exception: unknown, metadata?: unknown) => void };
    }
  ).errorReporting;

  errorReporting?.captureException(error, { context });
};

/**
 * Reads a persisted boolean flag from localStorage.
 */
const readStoredFlag = (key: string): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return localStorage.getItem(key) === "true";
  } catch (error) {
    reportPwaError(error, `readStoredFlag(${key})`);
    return false;
  }
};

/**
 * Persists a boolean flag in localStorage.
 */
const writeStoredFlag = (key: string, value: boolean): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(key, String(value));
  } catch (error) {
    reportPwaError(error, `writeStoredFlag(${key})`);
  }
};

/**
 * Persists a string value in localStorage.
 */
const writeStoredString = (key: string, value: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(key, value);
  } catch (error) {
    reportPwaError(error, `writeStoredString(${key})`);
  }
};

/**
 * Reads a string value from localStorage.
 */
const readStoredString = (key: string): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return localStorage.getItem(key);
  } catch (error) {
    reportPwaError(error, `readStoredString(${key})`);
    return null;
  }
};

/**
 * Detects the current mobile platform relevant to PWA installation.
 */
const detectPlatform = (): PwaPlatform => {
  if (typeof window === "undefined") {
    return "other";
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);

  if (isAndroid) {
    return "android";
  }

  if (isIos) {
    return "ios";
  }

  return "other";
};

/**
 * Exposes a global PWA installation state so mobile entry points can render install prompts safely.
 */
export const PwaInstallProvider = ({ children }: { children: ReactNode }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [hasInstalledApp, setHasInstalledApp] = useState(false);
  const [hasDismissedBanner, setHasDismissedBanner] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isNotificationPermissionRequesting, setIsNotificationPermissionRequesting] = useState(false);
  const [isNotificationSupported, setIsNotificationSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | "unsupported">("unsupported");
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PwaPlatform>("other");
  const tokenSyncInFlightRef = useRef<string | null>(null);

  /**
   * Registers an FCM token after permission is granted.
   */
  const registerPushToken = useCallback(async (): Promise<void> => {
    try {
      const token = await registerFirebasePushToken();

      if (!token) {
        return;
      }

      setFcmToken(token);
      writeStoredString(FCM_TOKEN_STORAGE_KEY, token);

      const alreadySyncedToken = readStoredString(FCM_TOKEN_SYNCED_KEY);

      if (alreadySyncedToken === token || tokenSyncInFlightRef.current === token) {
        return;
      }

      tokenSyncInFlightRef.current = token;

      const wasSynced = await syncFcmTokenToBackend(token);

      if (wasSynced) {
        writeStoredString(FCM_TOKEN_SYNCED_KEY, token);
      }
    } catch (error) {
      reportPwaError(error, "PwaInstallProvider.registerPushToken");
    } finally {
      tokenSyncInFlightRef.current = null;
    }
  }, []);

  /**
   * Syncs push setup when notifications are already granted outside the explicit CTA flow.
   */
  const ensurePushRegistration = useCallback(async (): Promise<void> => {
    if (typeof window === "undefined") {
      return;
    }

    const supportsNotifications = "Notification" in window && window.isSecureContext;

    if (!supportsNotifications || Notification.permission !== "granted") {
      return;
    }

    setIsNotificationSupported(true);
    setNotificationPermission("granted");
    await registerPushToken();
  }, [registerPushToken]);

  const refreshInstalledState = useCallback(async (): Promise<void> => {
    if (typeof window === "undefined") {
      return;
    }

    const navigatorWithPwa = window.navigator as NavigatorWithPwa;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches || navigatorWithPwa.standalone === true;

    let hasRelatedInstalledApp = false;

    if (navigatorWithPwa.getInstalledRelatedApps) {
      try {
        const relatedApps = await navigatorWithPwa.getInstalledRelatedApps();
        hasRelatedInstalledApp = relatedApps.length > 0;
      } catch (error) {
        reportPwaError(error, "PwaInstallProvider.getInstalledRelatedApps");
      }
    }

    setHasInstalledApp(isStandalone || hasRelatedInstalledApp || readStoredFlag(PWA_INSTALLED_KEY));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setPlatform(detectPlatform());
    setHasDismissedBanner(readStoredFlag(PWA_BANNER_DISMISSED_KEY));
    setFcmToken(readStoredString(FCM_TOKEN_STORAGE_KEY));

    const supportsNotifications = "Notification" in window && window.isSecureContext;
    setIsNotificationSupported(supportsNotifications);
    setNotificationPermission(supportsNotifications ? Notification.permission : "unsupported");

    if (supportsNotifications && Notification.permission === "granted") {
      void ensurePushRegistration();
    }

    void refreshInstalledState();

    const standaloneMediaQuery = window.matchMedia("(display-mode: standalone)");

    const handleBeforeInstallPrompt = (event: Event): void => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
    };

    const handleAppInstalled = (): void => {
      writeStoredFlag(PWA_INSTALLED_KEY, true);
      setHasInstalledApp(true);
      setDeferredPrompt(null);
      setHasDismissedBanner(false);
    };

    const handleStandaloneChange = (): void => {
      void refreshInstalledState();
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        void refreshInstalledState();
        void ensurePushRegistration();
      }
    };

    let permissionStatus: PermissionStatus | null = null;

    const attachPermissionListener = async (): Promise<void> => {
      try {
        const navigatorWithPwa = window.navigator as NavigatorWithPwa;

        if (!navigatorWithPwa.permissions?.query) {
          return;
        }

        permissionStatus = await navigatorWithPwa.permissions.query({
          name: "notifications" as PermissionName,
        });

        permissionStatus.onchange = () => {
          const currentPermission = Notification.permission;
          setNotificationPermission(currentPermission);

          if (currentPermission === "granted") {
            void ensurePushRegistration();
          }
        };
      } catch (error) {
        reportPwaError(error, "PwaInstallProvider.attachPermissionListener");
      }
    };

    void attachPermissionListener();

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    standaloneMediaQuery.addEventListener("change", handleStandaloneChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      standaloneMediaQuery.removeEventListener("change", handleStandaloneChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [ensurePushRegistration, refreshInstalledState]);

  /**
   * Hides the install banner until the user explicitly reinstalls or app state changes.
   */
  const dismissInstallBanner = useCallback((): void => {
    setHasDismissedBanner(true);
    writeStoredFlag(PWA_BANNER_DISMISSED_KEY, true);
  }, []);

  /**
   * Opens the browser install prompt on supported Android browsers.
   */
  const installPwa = useCallback(async (): Promise<void> => {
    if (!deferredPrompt) {
      return;
    }

    try {
      setIsInstalling(true);
      await deferredPrompt.prompt();

      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        writeStoredFlag(PWA_INSTALLED_KEY, true);
        setHasInstalledApp(true);
      } else {
        dismissInstallBanner();
      }
    } catch (error) {
      reportPwaError(error, "PwaInstallProvider.installPwa");
    } finally {
      setDeferredPrompt(null);
      setIsInstalling(false);
    }
  }, [deferredPrompt, dismissInstallBanner]);

  /**
   * Requests browser permission to display notifications.
   */
  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission | "unsupported"> => {
    if (typeof window === "undefined") {
      return "unsupported";
    }

    const supportsNotifications = "Notification" in window && window.isSecureContext;

    if (!supportsNotifications) {
      setIsNotificationSupported(false);
      setNotificationPermission("unsupported");
      return "unsupported";
    }

    try {
      setIsNotificationPermissionRequesting(true);

      if (Notification.permission === "granted") {
        setNotificationPermission("granted");
        await registerPushToken();
        return "granted";
      }

      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        await registerPushToken();
      }

      return permission;
    } catch (error) {
      reportPwaError(error, "PwaInstallProvider.requestNotificationPermission");
      return Notification.permission;
    } finally {
      setIsNotificationPermissionRequesting(false);
    }
  }, [registerPushToken]);

  /**
   * Handles the install CTA without requesting notification permissions.
   */
  const handleInstall = useCallback(async (): Promise<void> => {
    if (platform === "android" && deferredPrompt !== null) {
      await installPwa();
    }
  }, [deferredPrompt, installPwa, platform]);

  const contextValue = useMemo<PwaInstallContextValue>(() => {
    const isInstallBannerVisible =
      !hasInstalledApp &&
      !hasDismissedBanner &&
      !isAndroidApp() &&
      (platform === "ios" || (platform === "android" && deferredPrompt !== null));

    return {
      canPromptInstall: platform === "android" && deferredPrompt !== null,
      dismissInstallBanner,
      fcmToken,
      handleInstall,
      isHandleInstallLoading: isInstalling,
      installPwa,
      isNotificationPermissionRequesting,
      isNotificationSupported,
      isInstallBannerVisible,
      isInstalling,
      notificationPermission,
      platform,
      requestNotificationPermission,
    };
  }, [
    deferredPrompt,
    dismissInstallBanner,
    fcmToken,
    handleInstall,
    hasDismissedBanner,
    hasInstalledApp,
    installPwa,
    isInstalling,
    isNotificationSupported,
    notificationPermission,
    platform,
    requestNotificationPermission,
  ]);

  return <PwaInstallContext.Provider value={contextValue}>{children}</PwaInstallContext.Provider>;
};